import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Lock, Plus, Users, ChevronRight, BookOpen, UserPlus, Pencil, PauseCircle, PlayCircle, UserMinus } from "lucide-react";
import SidePanel from "./SidePanel";
import BulkStudentPicker from "./BulkStudentPicker";
import PlanCardActions from "./PlanCardActions";
import { toast } from "sonner";

const SLOTS = ["breakfast", "lunch", "snacks"];
const today = () => new Date().toISOString().slice(0, 10);

function statusBadge(plan) {
  if (Boolean(plan.archived)) return { label: "Archived", cls: "bg-amber-100 text-amber-800" };
  if (plan.active === false) return { label: "Inactive", cls: "bg-slate-200 text-slate-600" };
  const t = today();
  if (!plan.start_date || !plan.end_date) return { label: "Draft", cls: "bg-slate-100 text-slate-600" };
  if (t < plan.start_date) return { label: "Upcoming", cls: "bg-blue-100 text-blue-700" };
  if (t > plan.end_date) return { label: "Expired", cls: "bg-slate-100 text-slate-500" };
  return { label: "Active", cls: "bg-emerald-100 text-emerald-700" };
}

function isLocked(plan) {
  const t = today();
  return plan.start_date && plan.end_date && plan.start_date <= t && plan.end_date >= t;
}

function PlanCard({ plan, onClick, onEdit, templateName, studentCount, onChanged }) {
  const badge = statusBadge(plan);
  const locked = isLocked(plan);
  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      className={`text-left border rounded-2xl p-6 hover:shadow-md hover:-translate-y-0.5 transition-all group cursor-pointer ${plan.archived ? "bg-amber-50/40 border-amber-200" : "bg-slate-50 border-slate-200"}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-heading text-xl font-extrabold text-slate-900 tracking-tight">{plan.name}</h3>
          {plan.term && <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mt-1">{plan.term}</div>}
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${badge.cls}`}>
            {badge.label}
          </span>
          <button data-testid={`plan-edit-${plan.id}`} onClick={(e) => { e.stopPropagation(); onEdit(plan); }}
            className="p-1.5 rounded-full bg-slate-200 text-slate-700 hover:bg-slate-300" title="Edit plan">
            <Pencil size={11} />
          </button>
          <PlanCardActions plan={plan} resource="meal-plans" accentClass="bg-slate-200 text-slate-700"
            onChanged={onChanged} testid="sub" />
        </div>
      </div>
      <div className="text-xs text-slate-600 mb-3">
        {plan.start_date || "—"} → {plan.end_date || "—"}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mb-4">
        {(plan.slots || []).map((s) => (
          <span key={s} className="text-[10px] font-semibold uppercase px-2 py-0.5 bg-slate-200 text-slate-700 rounded">
            {s}
          </span>
        ))}
      </div>
      <div className="flex items-center justify-between text-sm border-t border-slate-200 pt-3">
        <div className="flex items-center gap-2 text-slate-600">
          <BookOpen size={14} />
          <span className="truncate max-w-[180px]">{templateName || "No menu linked"}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-slate-900 font-bold">
            <Users size={14} /> {studentCount ?? "—"}
          </div>
          {locked && <Lock size={14} className="text-amber-600" />}
          <ChevronRight size={16} className="text-slate-400 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </div>
  );
}

function PlanDetailPanel({ plan, onClose, templates, onPlanUpdated }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allowParent, setAllowParent] = useState(plan?.allow_parent_subscribe !== false);
  const [allowAdmin, setAllowAdmin] = useState(plan?.allow_admin_assign !== false);
  const [linkedId, setLinkedId] = useState(plan?.linked_template_id || "");
  const [showLockWarn, setShowLockWarn] = useState(null); // {newId, count}
  const [showPicker, setShowPicker] = useState(false);
  const locked = isLocked(plan);

  const loadStudents = () => {
    if (!plan?.id) return;
    setLoading(true);
    api.get(`/meal-plans/${plan.id}/subscribed-students`)
      .then(r => setStudents(Array.isArray(r.data) ? r.data : []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadStudents(); /* eslint-disable-next-line */ }, [plan?.id]);

  const liveStudentCount = students.filter(s => s.live_status === "active").length;

  const doLink = async (templateId) => {
    try {
      await api.post(`/meal-plans/${plan.id}/link-template`, { template_id: templateId });
      setLinkedId(templateId);
      toast.success("Weekly Menu linked");
      onPlanUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to link template");
    }
  };

  const tryLink = (newId) => {
    if (locked && liveStudentCount > 0 && newId !== linkedId) {
      setShowLockWarn({ newId, count: liveStudentCount });
    } else {
      doLink(newId);
    }
  };

  const subscribedStudentIds = useMemo(() => students.map(s => s.student_id), [students]);

  return (
    <SidePanel open={!!plan} onClose={onClose} accent="slate"
      title={
        <span className="flex items-center gap-2">
          {plan?.name}
          {locked && <span className="inline-flex items-center gap-1 text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"><Lock size={11} /> Term Live</span>}
        </span>
      }>
      {locked && (
        <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900">
          <strong>Term in progress.</strong> Editing this plan or its menu mid-term may affect {liveStudentCount} active students. You'll be asked to confirm.
        </div>
      )}

      <section className="mb-6">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Term & validity</h3>
        <div className="text-sm text-slate-700 mb-1"><strong>{plan?.term || "—"}</strong> · {plan?.start_date || "—"} to {plan?.end_date || "—"}</div>
        <div className="flex flex-wrap gap-1.5">
          {(plan?.slots || []).map(s => (
            <span key={s} className="text-[10px] font-semibold uppercase px-2 py-0.5 bg-slate-200 text-slate-700 rounded">{s}</span>
          ))}
          {plan?.non_veg_option && <span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Non-veg</span>}
        </div>
        {plan?.applicable_grades?.length > 0 && (
          <div className="text-[11px] text-slate-500 mt-2">Grades: {plan.applicable_grades.join(", ")}</div>
        )}
      </section>

      {plan?.menu_items?.length > 0 && (
        <section className="mb-6">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Menu items</h3>
          <div className="flex flex-wrap gap-1.5">
            {plan.menu_items.map((m, i) => (
              <span key={i} className="text-xs px-2 py-1 bg-slate-100 text-slate-800 rounded-lg">{m}</span>
            ))}
          </div>
        </section>
      )}

      <section className="mb-6">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Subscription channels</h3>
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" data-testid="toggle-parent-subscribe" checked={allowParent} onChange={e => setAllowParent(e.target.checked)} />
            Parents can self-subscribe
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" data-testid="toggle-admin-assign" checked={allowAdmin} onChange={e => setAllowAdmin(e.target.checked)} />
            Admin can manually assign
          </label>
        </div>
      </section>

      <section className="mb-6">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Linked Weekly Menu</h3>
        <select
          data-testid="link-template-select"
          value={linkedId}
          onChange={e => tryLink(e.target.value)}
          className="block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white"
        >
          <option value="">— No menu linked —</option>
          {templates.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        {linkedId && (
          <a href="/admin/weekly-menu" className="text-xs text-slate-600 underline mt-2 inline-block">Open template →</a>
        )}
      </section>

      {/* Add students — opens bulk picker overlay */}
      <section className="mb-5">
        <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-200">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-700 flex items-center gap-1">
              <UserPlus size={12} /> Add students to this plan
            </h3>
            <div className="text-[11px] text-slate-500 mt-0.5">Bulk picker with grade filter + quick chips · handles 1000+ students in one batch.</div>
          </div>
          <button data-testid="open-bulk-picker" onClick={() => setShowPicker(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">
            <Plus size={14} /> Bulk add
          </button>
        </div>
      </section>

      {showPicker && (
        <BulkStudentPicker
          planId={plan.id}
          planStart={plan.start_date}
          planEnd={plan.end_date}
          excludeIds={subscribedStudentIds}
          onAssigned={() => { loadStudents(); }}
          onClose={() => setShowPicker(false)}
        />
      )}

      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Subscribed students ({students.length})</h3>
        </div>
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {!loading && students.length === 0 && (
          <div className="p-6 border border-dashed border-slate-200 rounded-xl text-center text-sm text-slate-500">
            No students subscribed yet.
          </div>
        )}
        {!loading && students.length > 0 && (
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-2 text-[10px] uppercase font-bold">Name</th>
                  <th className="text-left p-2 text-[10px] uppercase font-bold">Grade</th>
                  <th className="text-left p-2 text-[10px] uppercase font-bold">Status</th>
                  <th className="p-2 text-[10px] uppercase font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s, i) => {
                  const isPaused = s.live_status === "paused";
                  const isCancelled = s.live_status === "cancelled";
                  return (
                    <tr key={s.student_plan_id || i} className="border-t border-slate-100" data-testid={`subscribed-student-${i}`}>
                      <td className="p-2">{s.student_name}<div className="text-[10px] text-slate-500">{s.student_id_display}</div></td>
                      <td className="p-2 text-slate-600">{s.grade || "—"}</td>
                      <td className="p-2">
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                          s.live_status === "active" ? "bg-emerald-100 text-emerald-700" :
                          s.live_status === "upcoming" ? "bg-blue-100 text-blue-700" :
                          s.live_status === "expired" ? "bg-slate-100 text-slate-500" :
                          s.live_status === "paused" ? "bg-amber-100 text-amber-700" :
                          "bg-rose-100 text-rose-700"
                        }`}>{s.live_status}</span>
                      </td>
                      <td className="p-2 text-right">
                        {!isCancelled && (
                          <div className="inline-flex items-center gap-1">
                            {isPaused ? (
                              <button data-testid={`sp-resume-${i}`} title="Resume"
                                onClick={async () => {
                                  try { await api.patch(`/meal-plans/student-plan/${s.student_plan_id}/resume`); toast.success("Resumed"); loadStudents(); }
                                  catch (e) { toast.error(e?.response?.data?.detail || "Resume failed"); }
                                }}
                                className="p-1 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200">
                                <PlayCircle size={12} />
                              </button>
                            ) : (
                              <button data-testid={`sp-hold-${i}`} title="Hold (pause)"
                                onClick={async () => {
                                  try { await api.patch(`/meal-plans/student-plan/${s.student_plan_id}/pause`); toast.success("Held"); loadStudents(); }
                                  catch (e) { toast.error(e?.response?.data?.detail || "Hold failed"); }
                                }}
                                className="p-1 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200">
                                <PauseCircle size={12} />
                              </button>
                            )}
                            <button data-testid={`sp-unassign-${i}`} title="Unassign (consumption preserved)"
                              onClick={async () => {
                                if (!window.confirm(`Remove ${s.student_name} from this plan? Their past consumption history will be preserved.`)) return;
                                try { await api.delete(`/meal-plans/student-plan/${s.student_plan_id}`); toast.success("Unassigned"); loadStudents(); }
                                catch (e) { toast.error(e?.response?.data?.detail || "Unassign failed"); }
                              }}
                              className="p-1 rounded-full bg-rose-100 text-rose-700 hover:bg-rose-200">
                              <UserMinus size={12} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Lock confirmation modal */}
      {showLockWarn && (
        <div className="fixed inset-0 bg-ink/40 flex items-center justify-center z-[60] p-4" data-testid="lock-confirm-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="font-heading text-lg font-extrabold text-ink mb-2 flex items-center gap-2"><Lock size={18} className="text-amber-600" /> Menu change during live term</h3>
            <p className="text-sm text-slate-700 mb-4">
              This subscription plan currently has <strong>{showLockWarn.count} active students</strong>. Changing the linked weekly menu mid-term will be visible to them immediately.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowLockWarn(null)} className="px-4 py-2 rounded-full text-sm font-semibold bg-cream-200 text-ink-700 hover:bg-cream-300">Cancel</button>
              <button data-testid="confirm-lock-change" onClick={() => { doLink(showLockWarn.newId); setShowLockWarn(null); }}
                className="px-4 py-2 rounded-full text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700">Yes, change anyway</button>
            </div>
          </div>
        </div>
      )}
    </SidePanel>
  );
}

export default function SubscriptionTab({ plans, templates, onCreate, onEdit, onPlanUpdated, showArchived }) {
  const [selected, setSelected] = useState(null);
  const [studentCounts, setStudentCounts] = useState({});

  useEffect(() => {
    // Lazy-load student counts for each plan (one shot)
    plans.forEach((p) => {
      api.get(`/meal-plans/${p.id}/subscribed-students`)
        .then(r => setStudentCounts(prev => ({ ...prev, [p.id]: Array.isArray(r.data) ? r.data.length : 0 })))
        .catch(() => {});
    });
  }, [plans]);

  const templateMap = useMemo(() => Object.fromEntries(templates.map(t => [t.id, t.name])), [templates]);

  if (plans.length === 0) {
    return (
      <div className="border-2 border-dashed border-slate-200 rounded-2xl p-12 text-center bg-slate-50/50">
        <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">{showArchived ? "No archived subscription plans" : "No subscription plans yet"}</h3>
        <p className="text-sm text-slate-500 mb-4">{showArchived ? "Archived plans will appear here." : "Create a plan that auto-feeds students for a whole term."}</p>
        {!showArchived && (
          <button onClick={() => onCreate("subscription")} data-testid="empty-create-subscription"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-800">
            <Plus size={14} /> Create Subscription Plan
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {plans.map(p => (
          <PlanCard
            key={p.id}
            plan={p}
            templateName={templateMap[p.linked_template_id]}
            studentCount={studentCounts[p.id]}
            onClick={() => setSelected(p)}
            onEdit={onEdit}
            onChanged={onPlanUpdated}
          />
        ))}
      </div>
      {selected && (
        <PlanDetailPanel
          plan={selected}
          onClose={() => setSelected(null)}
          templates={templates}
          onPlanUpdated={onPlanUpdated}
        />
      )}
    </>
  );
}
